import { useState } from "react";
import type { AdminUserListItemResponse } from "../../../types/adminTypes";
import { formatDate } from "../../../utils/DateUtils";
import { formatCityStateZip } from "../../../utils/LocationUtils";
import Button from "../../ui/Button/Button";
import AdminPillBadge from "../ui/AdminPillBadge";
import AdminTableShell from "../ui/AdminTableShell";
import AdminUserDetailModal, { type AdminUserDetailModalUserRef } from "./AdminUserDetailModal";
import AdminUsersFilters from "./AdminUsersFilters";
import AdminUsersTableRow from "./AdminUsersTableRow";
import { adminUsersColumns } from "./adminUsersColumns";

type AdminUsersTableProps = {
  users: AdminUserListItemResponse[];
  isLoading: boolean;
  canWrite: boolean;
  search: string;
  locationSearch: string;
  state: string;
  under18: boolean;
  skillSearch: string;
  role: string;
  accountType: string;
  teenPayoutSetup: string;
  status: string;
  emailVerified: string;
  guardianConnection: string;
  createdFromDate: string;
  createdToDate: string;
  pageSize: number;
  page: number;
  resultsCount: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onSearchChange: (value: string) => void;
  onLocationSearchChange: (value: string) => void;
  onStateChange: (value: string) => void;
  onUnder18Change: (value: boolean) => void;
  onSkillSearchChange: (value: string) => void;
  onRoleChange: (value: string) => void;
  onAccountTypeChange: (value: string) => void;
  onTeenPayoutSetupChange: (value: string) => void;
  onStatusChange: (value: string) => void;
  onEmailVerifiedChange: (value: string) => void;
  onGuardianConnectionChange: (value: string) => void;
  onCreatedFromDateChange: (value: string) => void;
  onCreatedToDateChange: (value: string) => void;
  onPageSizeChange: (value: number) => void;
  onPageChange: (nextPage: number) => void;
  onRequestTotalCount: () => void;
  onGenerateEmailList: () => void;
};

const AdminUsersTable = ({
  users,
  isLoading,
  canWrite,
  search,
  locationSearch,
  state,
  under18,
  skillSearch,
  role,
  accountType,
  teenPayoutSetup,
  status,
  emailVerified,
  guardianConnection,
  createdFromDate,
  createdToDate,
  pageSize,
  page,
  resultsCount,
  hasNextPage,
  hasPreviousPage,
  onSearchChange,
  onLocationSearchChange,
  onStateChange,
  onUnder18Change,
  onSkillSearchChange,
  onRoleChange,
  onAccountTypeChange,
  onTeenPayoutSetupChange,
  onStatusChange,
  onEmailVerifiedChange,
  onGuardianConnectionChange,
  onCreatedFromDateChange,
  onCreatedToDateChange,
  onPageSizeChange,
  onPageChange,
  onRequestTotalCount,
  onGenerateEmailList,
}: AdminUsersTableProps) => {
  const [selectedUser, setSelectedUser] = useState<AdminUserDetailModalUserRef | null>(null);
  const showNoResults = !isLoading && users.length === 0;

  const getDeletedAccountLabel = (user: AdminUserListItemResponse) => {
    const formerName = [user.deletionAudit?.firstName, user.deletionAudit?.lastName].filter(Boolean).join(" ");
    return `Deleted account${formerName ? ` (${formerName})` : ""}`;
  };

  const statusTone: Record<AdminUserListItemResponse["status"], "success" | "warning" | "danger"> = {
    ACTIVE: "success",
    PENDING: "warning",
    SUSPENDED: "warning",
    DELETED: "danger",
  };

  const toUserRef = (user: AdminUserListItemResponse): AdminUserDetailModalUserRef => ({
    id: user.id,
    firstName: user.status === "DELETED" ? "Deleted" : user.firstName,
    lastName: user.status === "DELETED" ? "Account" : user.lastName,
    email: user.status === "DELETED" ? undefined : user.email,
    status: user.status,
    deletedAt: user.deletedAt,
  });

  const getTeenStripeLabel = (user: AdminUserListItemResponse) => {
    if (!user.roles.includes("TEEN")) {
      return "N/A";
    }
    return user.teenPayoutReady ? "Ready" : "Not ready";
  };

  const getTeenStripeTone = (user: AdminUserListItemResponse) => {
    if (!user.roles.includes("TEEN")) {
      return "neutral";
    }
    return user.teenPayoutReady ? "success" : "warning";
  };

  return (
    <>
      <AdminTableShell
        columns={adminUsersColumns}
        filters={
          <AdminUsersFilters
            search={search}
            locationSearch={locationSearch}
            state={state}
            under18={under18}
            skillSearch={skillSearch}
            role={role}
            accountType={accountType}
            teenPayoutSetup={teenPayoutSetup}
            status={status}
            emailVerified={emailVerified}
            guardianConnection={guardianConnection}
            createdFromDate={createdFromDate}
            createdToDate={createdToDate}
            pageSize={pageSize}
            onSearchChange={onSearchChange}
            onLocationSearchChange={onLocationSearchChange}
            onStateChange={onStateChange}
            onUnder18Change={onUnder18Change}
            onSkillSearchChange={onSkillSearchChange}
            onRoleChange={onRoleChange}
            onAccountTypeChange={onAccountTypeChange}
            onTeenPayoutSetupChange={onTeenPayoutSetupChange}
            onStatusChange={onStatusChange}
            onEmailVerifiedChange={onEmailVerifiedChange}
            onGuardianConnectionChange={onGuardianConnectionChange}
            onCreatedFromDateChange={onCreatedFromDateChange}
            onCreatedToDateChange={onCreatedToDateChange}
            onPageSizeChange={onPageSizeChange}
            onRequestTotalCount={onRequestTotalCount}
            onGenerateEmailList={onGenerateEmailList}
          />
        }
        mobileCards={users.map((user) => (
          <article key={user.id} className="rounded-[1.5rem] border border-myteengig-border bg-white p-4 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold text-myteengig-text-primary">
                  {user.status === "DELETED"
                    ? getDeletedAccountLabel(user)
                    : `${user.firstName} ${user.lastName}`}
                </p>
                <p className="mt-1 break-all text-sm text-myteengig-text-muted">
                  {user.status === "DELETED" ? user.deletionAudit?.email ?? "Account email removed" : user.email}
                </p>
                <p className="mt-1 text-xs text-myteengig-text-muted">
                  {user.status === "DELETED"
                    ? user.deletedAt ? `Deleted ${formatDate(user.deletedAt)}` : "Closed account"
                    : user.emailVerified ? "Email verified" : "Email unverified"}
                </p>
                {user.status === "DELETED" ? (
                  <p className="mt-1 text-xs text-myteengig-text-muted">
                    User ID {user.id.slice(0, 8)}
                  </p>
                ) : null}
              </div>
              <AdminPillBadge tone={statusTone[user.status]}>{user.status}</AdminPillBadge>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              {(user.status === "DELETED" && user.deletionAudit?.roles?.length ? user.deletionAudit.roles : user.roles).map((roleValue) => (
                <AdminPillBadge key={roleValue}>{roleValue}</AdminPillBadge>
              ))}
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div className="rounded-2xl bg-myteengig-background/70 px-4 py-3">
                <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Location</p>
                <p className="mt-1 text-sm text-myteengig-text-primary">
                  {formatCityStateZip(user, "No location")}
                </p>
              </div>
              <div className="rounded-2xl bg-myteengig-background/70 px-4 py-3">
                <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Created</p>
                <p className="mt-1 text-sm text-myteengig-text-primary">{formatDate(user.createdAt)}</p>
              </div>
              <div className="rounded-2xl bg-myteengig-background/70 px-4 py-3">
                <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Teen Stripe</p>
                <div className="mt-2">
                  <AdminPillBadge tone={getTeenStripeTone(user) as "neutral" | "success" | "warning"}>
                    {getTeenStripeLabel(user)}
                  </AdminPillBadge>
                </div>
              </div>
            </div>

            <div className="mt-4 flex justify-end">
              <Button variant="outline" size="sm" className="w-full sm:w-auto" onClick={() => setSelectedUser(toUserRef(user))}>
                View
              </Button>
            </div>
          </article>
        ))}
        rows={users.map((user) => (
          <AdminUsersTableRow key={user.id} user={user} onView={(selected) => setSelectedUser(toUserRef(selected))} />
        ))}
        isLoading={isLoading}
        isEmpty={showNoResults}
        emptyMessage="No users found for the current filters."
        page={page}
        pageSize={pageSize}
        resultsCount={resultsCount}
        hasNextPage={hasNextPage}
        hasPreviousPage={hasPreviousPage}
        onPageChange={onPageChange}
      />
      <AdminUserDetailModal
        user={selectedUser}
        isOpen={selectedUser !== null}
        canWrite={canWrite}
        onClose={() => setSelectedUser(null)}
        onOpenUser={setSelectedUser}
      />
    </>
  );
};

export default AdminUsersTable;
